import { FC, useMemo } from 'react';
import VideoIcon from 'icon/play_fill.svg';
import { ArticleDetailUtil, EArticleClass, SearchWord, UserDetailUtil } from 'shared/models';
import { useSEOHref } from '~/utils/build-seo-url';
import { Link } from '@remix-run/react';
import { formatNumByRegion, useNumFormatter } from '~/utils/format';
import { kepQueryFormat } from 'shared/utils';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useTranslation } from 'react-i18next';
import { TSimpleDualColumnCardInfo } from '..';
import { Region } from '@lemon8/web-app-shared/i18n';

import './style.scss';

export interface DualColumnCardProps {
  cardInfo: TSimpleDualColumnCardInfo;
  words: SearchWord[];
  pos: number;
}

export const DualColumnCard: FC<DualColumnCardProps> = ({ cardInfo, words, pos }) => {
  const { article, author } = cardInfo;
  const { articleClass, title = '', shortContent = '', diggCount, commentCount, readCount } = article;
  const src = articleClass === EArticleClass.GALLERY ? article.imageList[0].url : article.largeImage.url;
  const authorHref = useSEOHref(UserDetailUtil.getPath(author));
  const postHref = useSEOHref(ArticleDetailUtil.getArticleHref(article));
  const formatNum = useNumFormatter();
  const { region } = useAtomValue($Locale);
  const { t } = useTranslation();

  const renderInternalLink = (slicedWords: SearchWord[]): JSX.Element => {
    return (
      <div className="dual-column-card-info-internal-link-group">
        {slicedWords.map((wordItem, index) => {
          const { word = '' } = wordItem;
          const href = `/discover/${kepQueryFormat(word)}?region=${region}`;
          return (
            <Link key={`${word}-${index}`} to={href} className="dual-column-card-info-internal-link-group-item">
              <strong>#{word}</strong>
            </Link>
          );
        })}
      </div>
    );
  };

  const renderInternalLinkGroup = (): JSX.Element | null => {
    const { length } = words;
    switch (pos) {
      case 0:
        return <>{renderInternalLink(words.slice(0, length / 2))}</>;
      case 1:
        return <>{renderInternalLink(words.slice(length / 2, length))}</>;
      default:
        return null;
    }
  };

  const panelData = useMemo(() => {
    return [
      { label: t('article_view_count'), count: readCount },
      { label: t('like_count_number'), count: diggCount },
      { label: t('comment'), count: commentCount },
    ];
  }, [diggCount, commentCount, readCount]);

  return (
    <div className="dual-column-card">
      <div className="dual-column-card-main">
        {articleClass === EArticleClass.VIDEO && (
          <div className="dual-column-card-main-video-badge">
            <VideoIcon />
          </div>
        )}
        <div className="dual-column-card-main-cover">
          <img
            src={src}
            loading="lazy"
            alt={`${t('view_count_plural', { num: readCount }).replace(
              '%d',
              formatNumByRegion(readCount, region as unknown as Region, t),
            )}, ${t('like_count_plural', { num: diggCount }).replace(
              '%d',
              formatNumByRegion(diggCount, region as unknown as Region, t),
            )} ${t('seo_keyword_page_postedBy')} ${author.nickName}: ${title}`}
          />
        </div>
      </div>
      <div className="dual-column-card-info">
        <Link to={authorHref} className="dual-column-card-info-author">
          <img
            loading="lazy"
            className="dual-column-card-info-author-avatar"
            src={author.avatar}
            alt={author.nickName || ''}
          />
          <div className="dual-column-card-info-author-name">{author.nickName}</div>
        </Link>
        <Link to={postHref} className="dual-column-card-info-title">
          <h2>{title}</h2>
        </Link>
        <div className="dual-column-card-info-content">{shortContent}</div>
        <div className="dual-column-card-info-panel">
          {panelData.map((item, index) => {
            const { label, count } = item;
            return (
              <div key={index} className="dual-column-card-info-panel-item">
                <div className="dual-column-card-info-panel-item-count">{formatNum(count)}</div>
                <div className="dual-column-card-info-panel-item-label">{label}</div>
              </div>
            );
          })}
        </div>
        {renderInternalLinkGroup()}
      </div>
    </div>
  );
};
